<template>
  <BaseDialog contentClass="sync-profile-dialog" ref="dialog" :show-actions="true">
    <template slot="title">{{
      isEdit ? $t('page.admin-sync-profiles.dialog.edit-title') : $t('page.admin-sync-profiles.dialog.add-title')
    }}</template>
    <template slot="content">
      <form @click.prevent @keydown.enter.prevent>
        <FormInput
          :label="$t('page.admin-sync-profiles.fields.name')"
          :is-required="true"
          has-gray-background
          v-model="form.name"
          :error="nameError"
          @input="touch('name')"
        />

        <div class="sync-profile-dialog__row">
          <div
            v-tooltip="
              isEdit ? { content: $t('page.admin-sync-profiles.fields.hint-type-locked'), placement: 'top' } : null
            "
          >
            <BaseSelect
              name="type"
              :label="$t('page.admin-sync-profiles.fields.type')"
              :is-required="true"
              :items="typeOptions"
              item-text="label"
              item-value="value"
              :value="form.type"
              @change="(v) => (form.type = v)"
              :hint="$t('page.admin-sync-profiles.fields.hint-type')"
              :is-disabled="isEdit"
            />
          </div>
          <BaseSelect
            name="syncHttpMethod"
            :label="$t('page.admin-sync-profiles.fields.sync-http-method')"
            :is-required="true"
            :items="httpMethodOptions"
            item-text="label"
            item-value="value"
            :value="form.syncHttpMethod"
            @change="(v) => (form.syncHttpMethod = v)"
          />
        </div>

        <template v-if="targetCrmFieldVisible">
          <BaseSelect
            name="targetCrm"
            :label="$t('page.admin-sync-profiles.fields.target-crm')"
            :items="targetCrmOptions"
            item-text="label"
            item-value="value"
            :value="form.targetCrm != null ? form.targetCrm : ''"
            @change="(v) => (form.targetCrm = v || null)"
            :hint="$t('page.admin-sync-profiles.fields.hint-target-crm')"
          />
        </template>

        <FormInput
          :label="$t('page.admin-sync-profiles.fields.sync-url')"
          :is-required="true"
          has-gray-background
          v-model="form.syncUrl"
          :error="syncUrlError"
          @input="touch('syncUrl')"
        />

        <div class="sync-profile-dialog__section">
          <div class="sync-profile-dialog__section-title">
            {{ $t('page.admin-sync-profiles.fields.auth-section') }}
          </div>

          <BaseSelect
            name="authType"
            :label="$t('page.admin-sync-profiles.fields.auth-type')"
            :is-required="true"
            :items="authTypeOptions"
            item-text="label"
            item-value="value"
            :value="form.authType"
            :is-disabled="authTypeFieldLocked"
            @change="(v) => (form.authType = v)"
            :hint="authTypeHint"
          />

          <FormInput
            v-if="form.authType === 'OAUTH2'"
            :label="$t('page.admin-sync-profiles.fields.auth-url')"
            :is-required="true"
            has-gray-background
            v-model="form.authUrl"
            :hint="$t('page.admin-sync-profiles.fields.hint-auth-url')"
            :error="authUrlError"
            @input="touch('authUrl')"
          />

          <div v-if="credentialsFieldVisible" class="sync-profile-dialog__field">
            <div class="sync-profile-dialog__label-row">
              <FormLabel :is-required="credentialsRequired">{{ credentialsLabel }}</FormLabel>
              <button
                v-if="form.authType === 'OAUTH2' || form.authType === 'API_KEY'"
                type="button"
                class="sync-profile-dialog__template-btn"
                @click="fillCredentialsTemplate"
              >
                {{ $t('page.admin-sync-profiles.fields.auth-credentials-fill-template') }}
              </button>
            </div>
            <textarea
              class="sync-profile-dialog__textarea"
              :class="{ 'sync-profile-dialog__textarea--error': authCredentialsError }"
              v-model="form.authCredentialsClear"
              :placeholder="credentialsPlaceholder"
              :rows="form.authType === 'BASIC' ? 1 : 4"
              @input="touch('authCredentials')"
            />
            <span class="sync-profile-dialog__hint">{{ credentialsHint }}</span>
            <div v-if="authCredentialsError" class="sync-profile-dialog__error">{{ authCredentialsError }}</div>
          </div>
        </div>

        <div class="sync-profile-dialog__section">
          <div class="sync-profile-dialog__section-title">
            {{ $t('page.admin-sync-profiles.fields.behavior-section') }}
          </div>
          <div class="sync-profile-dialog__toggles">
            <div class="sync-profile-dialog__toggle-row">
              <div class="sync-profile-dialog__toggle-label">
                <span>{{ $t('page.admin-sync-profiles.fields.active') }}</span>
                <BaseIcon
                  v-tooltip="{ content: $t('page.admin-sync-profiles.fields.hint-active'), placement: 'top' }"
                  name="info"
                  class="sync-profile-dialog__toggle-info"
                />
              </div>
              <BaseToggle :value="form.active" @click.native="form.active = !form.active" />
            </div>
            <div class="sync-profile-dialog__toggle-row">
              <div class="sync-profile-dialog__toggle-label">
                <span :class="{ 'sync-profile-dialog__toggle-label--disabled': !quoteSyncSupported }">
                  {{ $t('page.admin-sync-profiles.fields.quote-sync-enabled') }}
                </span>
                <BaseIcon
                  v-tooltip="{
                    content: quoteSyncSupported
                      ? $t('page.admin-sync-profiles.fields.hint-quote-sync-enabled')
                      : $t('page.admin-sync-profiles.fields.hint-quote-sync-unavailable'),
                    placement: 'top',
                  }"
                  name="info"
                  class="sync-profile-dialog__toggle-info"
                />
              </div>
              <BaseToggle
                :value="form.quoteSyncEnabled"
                :disabled="!quoteSyncSupported"
                @click.native="toggleQuoteSync"
              />
            </div>
            <div class="sync-profile-dialog__toggle-row">
              <div class="sync-profile-dialog__toggle-label">
                <span>{{ $t('page.admin-sync-profiles.fields.auto-sync') }}</span>
                <BaseIcon
                  v-tooltip="{ content: $t('page.admin-sync-profiles.fields.hint-auto-sync'), placement: 'top' }"
                  name="info"
                  class="sync-profile-dialog__toggle-info"
                />
              </div>
              <BaseToggle :value="form.autoSync" @click.native="form.autoSync = !form.autoSync" />
            </div>
            <div class="sync-profile-dialog__toggle-row">
              <div class="sync-profile-dialog__toggle-label">
                <span>{{ $t('page.admin-sync-profiles.fields.sync-on-update') }}</span>
                <BaseIcon
                  v-tooltip="{ content: $t('page.admin-sync-profiles.fields.hint-sync-on-update'), placement: 'top' }"
                  name="info"
                  class="sync-profile-dialog__toggle-info"
                />
              </div>
              <BaseToggle :value="form.syncOnUpdate" @click.native="form.syncOnUpdate = !form.syncOnUpdate" />
            </div>
          </div>
        </div>

        <div class="sync-profile-dialog__actions">
          <BaseRoundButton
            v-if="isEdit"
            :size="36"
            icon="trash-2"
            is-white
            is-red
            :tooltip-text="$t('actions.delete')"
            @click.native="$emit('delete-requested', profile)"
          />
          <div class="sync-profile-dialog__actions-right">
            <FormButton is-outlined @click.native="closeDialog">{{ $t('actions.cancel') }}</FormButton>
            <FormButton @click.native="onSubmit" :disabled="isFormInvalid">{{ $t('actions.save') }}</FormButton>
          </div>
        </div>
      </form>
    </template>
  </BaseDialog>
</template>

<script>
import BaseDialog from '@/components/base/BaseDialog';
import BaseIcon from '@/components/base/BaseIcon';
import BaseRoundButton from '@/components/base/BaseRoundButton';
import BaseSelect from '@/components/base/BaseSelect';
import BaseToggle from '@/components/base/BaseToggle';
import FormButton from '@/components/form/FormButton';
import FormInput from '@/components/form/FormInput';
import FormLabel from '@/components/form/FormLabel';
import { getProfileTypeOptions, supportsQuoteSync, TARGET_CRM_OPTIONS } from '@/util/sync-profile-util';
import { mapActions } from 'vuex';

// "Insert template" presets, keyed by authType.
const CREDENTIALS_TEMPLATES = {
  OAUTH2: {
    grant_type: 'client_credentials',
    client_id: '',
    client_secret: '',
    scope: 'https://yourorg.crm.dynamics.com/.default',
  },
  API_KEY: {
    in: 'query',
    name: '',
    value: '',
  },
};

// Mirrors the back-end's `OpportunitySyncProfile.ALLOWED_AUTH` allowlist. The back-end
// rejects POST/PUT with a 400 when an authType isn't permitted for the chosen profile
// type. Keep this list in sync with the back-end source of truth — if a new CRM type
// is added or an existing one's allowed auth types change, update here AND there.
// (We hardcode instead of adding a dedicated endpoint because the table is small and
// rarely changes; the back-end still validates server-side as the final word.)
const ALLOWED_AUTH_BY_TYPE = {
  MS_DYNAMICS: ['OAUTH2'],
  MS_DYNAMICS_CUSTOM: ['OAUTH2'],
  MS_BUSINESS_CENTRAL: ['BASIC'],
  WEBHOOK: ['NONE', 'OAUTH2', 'API_KEY'],
};
// Default authType to land on for each profile type. For single-allowed types this is
// the only valid value; for WEBHOOK (multi-allowed) it's the "least committal" default.
const DEFAULT_AUTH_BY_TYPE = {
  MS_DYNAMICS: 'OAUTH2',
  MS_DYNAMICS_CUSTOM: 'OAUTH2',
  MS_BUSINESS_CENTRAL: 'BASIC',
  WEBHOOK: 'NONE',
};

export default {
  name: 'SyncProfileDialog',
  components: { BaseDialog, BaseIcon, BaseRoundButton, BaseSelect, BaseToggle, FormButton, FormInput, FormLabel },
  data() {
    return {
      profile: null,
      form: this.emptyForm(),
      submitted: false,
      touched: {},
    };
  },
  computed: {
    isEdit() {
      return this.profile !== null;
    },
    quoteSyncSupported() {
      return supportsQuoteSync(this.form.type);
    },
    isFormInvalid() {
      return (
        !this.form.name ||
        !this.form.type ||
        !this.form.syncUrl ||
        !this.form.syncHttpMethod ||
        !this.form.authType ||
        (this.form.authType === 'OAUTH2' && !this.form.authUrl) ||
        (this.credentialsRequired && !this.form.authCredentialsClear) ||
        !!this.credentialsContentError
      );
    },
    nameError() {
      if (this.form.name) return null;
      return this.submitted || this.touched.name ? this.$t('page.admin-sync-profiles.fields.name-required') : null;
    },
    syncUrlError() {
      if (this.form.syncUrl) return null;
      return this.submitted || this.touched.syncUrl
        ? this.$t('page.admin-sync-profiles.fields.sync-url-required')
        : null;
    },
    authUrlError() {
      if (this.form.authType !== 'OAUTH2' || this.form.authUrl) return null;
      return this.submitted || this.touched.authUrl
        ? this.$t('page.admin-sync-profiles.fields.auth-url-required')
        : null;
    },
    authCredentialsError() {
      if (!this.credentialsFieldVisible) return null;
      if (this.credentialsRequired && !this.form.authCredentialsClear) {
        return this.submitted || this.touched.authCredentials
          ? this.$t('page.admin-sync-profiles.fields.auth-credentials-required')
          : null;
      }
      // Client-side mirror of the credential-content rules; the BE still validates and is the source of truth.
      if (this.credentialsContentError) {
        return this.submitted || this.touched.authCredentials ? this.credentialsContentError : null;
      }
      return null;
    },
    credentialsContentError() {
      // Only the JSON auth types have a cheap, unambiguous content check. BASIC is plaintext
      // (username:password) — its format is left to the BE.
      const isJsonType = this.form.authType === 'OAUTH2' || this.form.authType === 'API_KEY';
      if (!isJsonType || !this.form.authCredentialsClear) return null;
      let parsed;
      try {
        parsed = JSON.parse(this.form.authCredentialsClear);
      } catch (e) {
        return this.$t('page.admin-sync-profiles.fields.auth-credentials-invalid-json');
      }
      // API_KEY only: valuePrefix is header-only.
      if (this.form.authType === 'API_KEY' && parsed && parsed.valuePrefix != null && parsed.in !== 'header') {
        return this.$t('page.admin-sync-profiles.fields.auth-credentials-value-prefix-header-only');
      }
      return null;
    },
    credentialsFieldVisible() {
      return this.form.authType === 'OAUTH2' || this.form.authType === 'BASIC' || this.form.authType === 'API_KEY';
    },
    authTypeChanged() {
      return this.isEdit && this.profile != null && this.form.authType !== this.profile.authType;
    },
    credentialsRequired() {
      // Required for any non-NONE type on create, and on edit only when the auth type changed
      // (the BE won't carry old credentials across a type change).
      if (!this.credentialsFieldVisible) return false;
      return !this.isEdit || this.authTypeChanged;
    },
    credentialsLabel() {
      if (this.form.authType === 'BASIC') return this.$t('page.admin-sync-profiles.fields.auth-credentials-basic');
      if (this.form.authType === 'API_KEY') return this.$t('page.admin-sync-profiles.fields.auth-credentials-api-key');
      return this.$t('page.admin-sync-profiles.fields.auth-credentials');
    },
    credentialsPlaceholder() {
      // "Keep existing" only applies on edit when the auth type is unchanged.
      if (this.isEdit && !this.authTypeChanged) {
        return this.$t('page.admin-sync-profiles.fields.auth-credentials-placeholder-edit');
      }
      if (this.form.authType === 'BASIC') {
        return this.$t('page.admin-sync-profiles.fields.auth-credentials-placeholder-basic');
      }
      if (this.form.authType === 'API_KEY') {
        return this.$t('page.admin-sync-profiles.fields.auth-credentials-placeholder-api-key');
      }
      return this.$t('page.admin-sync-profiles.fields.auth-credentials-placeholder-add');
    },
    credentialsHint() {
      if (this.form.authType === 'BASIC') return this.$t('page.admin-sync-profiles.fields.hint-auth-credentials-basic');
      if (this.form.authType === 'API_KEY')
        return this.$t('page.admin-sync-profiles.fields.hint-auth-credentials-api-key');
      return this.$t('page.admin-sync-profiles.fields.hint-auth-credentials');
    },
    authTypeHint() {
      if (this.form.authType === 'NONE') return this.$t('page.admin-sync-profiles.fields.hint-auth-type-none');
      if (this.form.authType === 'OAUTH2') return this.$t('page.admin-sync-profiles.fields.hint-auth-type-oauth2');
      if (this.form.authType === 'BASIC') return this.$t('page.admin-sync-profiles.fields.hint-auth-type-basic');
      if (this.form.authType === 'API_KEY') return this.$t('page.admin-sync-profiles.fields.hint-auth-type-api-key');
      return '';
    },
    typeOptions() {
      // When editing an existing profile of a deprecated type (e.g. legacy MS_DYNAMICS_CUSTOM),
      // include that type so the select can render its label. The field is disabled on edit,
      // so this is purely about display — the user can't actually switch to a deprecated type.
      const include = this.isEdit && this.profile ? [this.profile.type] : [];
      return getProfileTypeOptions(include);
    },
    targetCrmFieldVisible() {
      // For MS_DYNAMICS / MS_BUSINESS_CENTRAL, the back-end auto-derives targetCrm from the type.
      // Only WEBHOOK and the legacy MS_DYNAMICS_CUSTOM let the user pick.
      return this.form.type === 'WEBHOOK' || this.form.type === 'MS_DYNAMICS_CUSTOM';
    },
    authTypeOptions() {
      // Filter the dropdown by the profile-type allowlist. Disallowed entries are hidden
      // (not disabled) so users aren't tempted to click them and wonder why they grey out.
      const allAuthTypes = [
        { label: 'None', value: 'NONE' },
        { label: 'OAuth 2', value: 'OAUTH2' },
        { label: 'Basic (HTTP)', value: 'BASIC' },
        { label: 'API Key', value: 'API_KEY' },
      ];
      const allowed = ALLOWED_AUTH_BY_TYPE[this.form.type] || allAuthTypes.map((o) => o.value);
      return allAuthTypes.filter((o) => allowed.includes(o.value));
    },
    authTypeFieldLocked() {
      // When the profile type allows only one auth type, the dropdown becomes a list of
      // one — disable it so the user sees the value but isn't presented with a pointless
      // pick. (`isEdit` already locks the type field, which transitively locks this when
      // editing an MS_DYNAMICS / MS_DYNAMICS_CUSTOM / MS_BUSINESS_CENTRAL profile.)
      return this.authTypeOptions.length <= 1;
    },
    httpMethodOptions() {
      return [
        { label: 'POST', value: 'POST' },
        { label: 'PUT', value: 'PUT' },
        { label: 'PATCH', value: 'PATCH' },
        { label: 'GET', value: 'GET' },
        { label: 'DELETE', value: 'DELETE' },
      ];
    },
    targetCrmOptions() {
      return TARGET_CRM_OPTIONS.map((opt) => ({
        ...opt,
        label: opt.label ?? this.$t('page.admin-sync-profiles.fields.target-crm-none'),
      }));
    },
  },
  watch: {
    'form.type'(val) {
      if (!supportsQuoteSync(val)) {
        this.form.quoteSyncEnabled = false;
      }
      // Keep targetCrm only for the types where the user actually picks it.
      if (val !== 'WEBHOOK' && val !== 'MS_DYNAMICS_CUSTOM') {
        this.form.targetCrm = null;
      }
      // Reconcile authType against the new profile type's allowlist (mirrors back-end's
      // ALLOWED_AUTH). If the current selection is still valid for the new type — e.g.
      // OAUTH2 carrying over from MS_DYNAMICS to WEBHOOK — leave it alone. Otherwise
      // swap to the new type's default so the form doesn't sit in an invalid state.
      // Skip on edit — Vue 2 watchers don't fire on the initial assignment anyway, but
      // the guard makes the intent explicit.
      if (!this.isEdit) {
        const allowed = ALLOWED_AUTH_BY_TYPE[val] || [];
        if (allowed.length && !allowed.includes(this.form.authType)) {
          this.form.authType = DEFAULT_AUTH_BY_TYPE[val] || allowed[0];
        }
      }
    },
    'form.authType'(val) {
      if (val !== 'OAUTH2') {
        this.form.authUrl = '';
      }
      // Credentials format differs per auth type (JSON for OAUTH2, user:pass for BASIC, n/a for NONE),
      // so stale input from the previous auth type is invalid — clear on any switch.
      // Symmetric with authUrl / targetCrm clearing.
      this.form.authCredentialsClear = '';
    },
  },
  methods: {
    ...mapActions('adminSyncProfiles', ['createSyncProfile', 'updateSyncProfile']),
    fillCredentialsTemplate() {
      const template = CREDENTIALS_TEMPLATES[this.form.authType];
      if (template) {
        this.form.authCredentialsClear = JSON.stringify(template, null, 2);
      }
    },
    toggleQuoteSync() {
      if (this.quoteSyncSupported) {
        this.form.quoteSyncEnabled = !this.form.quoteSyncEnabled;
      }
    },
    emptyForm() {
      return {
        name: '',
        type: 'MS_DYNAMICS',
        targetCrm: null,
        syncHttpMethod: 'POST',
        syncUrl: '',
        authType: 'OAUTH2',
        authUrl: '',
        authCredentialsClear: '',
        quoteSyncEnabled: false,
        autoSync: false,
        syncOnUpdate: false,
        active: true,
      };
    },
    touch(field) {
      this.$set(this.touched, field, true);
    },
    openDialog(profile = null) {
      this.submitted = false;
      this.touched = {};
      this.profile = profile;
      if (profile) {
        this.form = {
          name: profile.name,
          type: profile.type,
          targetCrm: profile.targetCrm ?? null,
          syncHttpMethod: profile.syncHttpMethod,
          syncUrl: profile.syncUrl,
          authType: profile.authType,
          authUrl: profile.authUrl || '',
          authCredentialsClear: '',
          quoteSyncEnabled: profile.quoteSyncEnabled,
          autoSync: profile.autoSync,
          syncOnUpdate: profile.syncOnUpdate,
          active: profile.active,
        };
      } else {
        this.form = this.emptyForm();
      }
      this.$refs.dialog.openModal();
    },
    closeDialog() {
      this.$refs.dialog.isModalOpen = false;
    },
    onSubmit() {
      this.submitted = true;
      if (this.isFormInvalid) return;
      const payload = { ...this.form };
      if (!payload.authCredentialsClear) {
        delete payload.authCredentialsClear;
      }
      if (payload.authType !== 'OAUTH2') {
        delete payload.authUrl;
      }
      if (payload.authType === 'NONE') {
        delete payload.authCredentialsClear;
      }
      // For non-webhook-style types the back-end auto-derives targetCrm from the type,
      // so sending it has no effect. Strip it to keep the payload clean.
      if (payload.type !== 'WEBHOOK' && payload.type !== 'MS_DYNAMICS_CUSTOM') {
        delete payload.targetCrm;
      }

      const action = this.isEdit
        ? this.updateSyncProfile({ profileId: this.profile.id, profile: payload })
        : this.createSyncProfile(payload);

      action
        .then(() => {
          this.$toast.success(this.$t('page.admin-sync-profiles.dialog.success'));
          this.$emit('action-succeeded');
          this.closeDialog();
        })
        .catch((err) => {
          // Surface back-end 400 messages verbatim (same pattern as FieldMappingDialog).
          // Covers e.g. "Profile type cannot be changed after creation" and auth/url validation.
          const backendMessage =
            (err && err.response && err.response.data && (err.response.data.message || err.response.data.error)) ||
            null;
          this.$toast.error(backendMessage || this.$t('page.admin-sync-profiles.dialog.error'));
        });
    },
  },
};
</script>

<style lang="scss">
.base-dialog__sync-profile-dialog {
  height: auto;
  width: 620px;

  .base-dialog__content {
    padding: 32px;
    background: $color-white !important;
  }

  .close-icon__container {
    display: none;
  }

  // Give every field (FormInput or BaseSelect) a consistent bottom margin so there is
  // always breathing room between fields, even when no hint or error is shown.
  .form-input {
    padding-top: 20px;
    padding-bottom: 0;
    margin-bottom: 14px;
  }

  .base-select {
    margin-bottom: 14px;
  }

  // Make hints and errors flow in the document so they push content down instead of
  // overflowing upward into the field above (the original absolute bottom: 4px behaviour).
  .form-input__hint,
  .form-input__error-msg {
    position: static;
    display: block;
    margin-top: 4px;
  }

  // Allow BaseSelect to grow when hint text wraps beyond one line.
  // !important is required because .base-select:not(.is-compact) { height: 90px } in
  // BaseSelect.vue has equal specificity and may be injected after this stylesheet.
  .base-select:not(.is-compact) {
    height: auto !important;
    min-height: 90px;
  }
}

.sync-profile-dialog {
  &__row {
    display: grid;
    grid-template-columns: 2fr 1fr;
    gap: 12px;
    margin-bottom: 4px;
  }

  &__section {
    border: 1px solid $color-border;
    border-radius: 6px;
    padding: 10px 14px 6px;
    margin-bottom: 12px;
  }

  &__section-title {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: $color-normal-pale;
    margin-bottom: 2px;
  }

  &__field {
    margin-bottom: 12px;
  }

  &__label-row {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    margin-bottom: 4px;
  }

  &__template-btn {
    font-size: 12px;
    color: $color-accent;
    background: none;
    border: none;
    cursor: pointer;
    padding: 0;
    text-decoration: underline;

    &:hover {
      opacity: 0.8;
    }
  }

  &__textarea {
    width: 100%;
    border: 1px solid $color-border;
    border-radius: 4px;
    padding: 8px;
    font-size: 14px;
    font-family: monospace;
    resize: vertical;
    background: $color-neutral-pale;
    color: $color-normal;

    &:focus {
      outline: none;
      border-color: $color-accent;
    }

    &--error {
      border-color: $color-error;
    }
  }

  &__error {
    font-size: 12px;
    color: $color-error;
    margin-top: 3px;
    line-height: 1.4;
  }

  &__hint {
    display: block;
    color: $color-neutral;
    font-size: 12px;
    margin-top: 4px;
  }

  &__toggles {
    display: flex;
    flex-direction: column;
    gap: 14px;
    padding: 4px 0 2px;
  }

  &__toggle-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
  }

  &__toggle-label {
    display: flex;
    align-items: center;
    gap: 6px;

    > span {
      font-size: 14px;
      font-weight: 500;
      color: $color-normal;
      transition: color 150ms ease;

      &--disabled {
        color: $color-neutral;
      }
    }
  }

  &__toggle-info {
    width: 14px;
    height: 14px;
    flex-shrink: 0;
    color: $color-normal-pale;
    cursor: help;
    transition: color 150ms ease;

    &:hover {
      color: $color-accent;
    }
  }

  &__actions {
    margin-top: 12px;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  &__actions-right {
    display: flex;
    gap: 12px;
  }
}
</style>
